// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminJobLog.js
// Admin: Job Log - recent job runs (EOD bars first), the tickers each run skipped and why, an AI
// "why were these skipped?" research button (woody-jobs skip_review.py, web search, model switchable)
// and the is_active switch per ticker. API: backend/joblog.py (@admin_required).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 16px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)',
             padding: '16px 20px', marginBottom: '16px' },
  title:   { fontSize: '14px', fontWeight: 600, marginBottom: '10px' },
  row:     { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '8px 0',
             borderBottom: '1px solid var(--border)', fontSize: '13px' },
  mono:    { fontFamily: "'DM Mono', monospace" },
  meta:    { color: 'var(--text-muted)', fontSize: '12px' },
  btn:     { padding: '4px 12px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px',
             fontWeight: 500, background: 'var(--accent-dim)', color: 'var(--accent)' },
  select:  { padding: '6px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
             background: 'var(--bg)', color: 'var(--text)', maxWidth: '100%' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  answer:  { flexBasis: '100%', fontSize: '12px', lineHeight: 1.5, color: 'var(--text)' },
};

const when = t => t ? new Date(t).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const statusColor = st => ({ ok: 'var(--success, #2e9e5b)', failed: 'var(--error)', running: 'var(--accent)' }[st]);
const verdictColor = v => ({ delisted: 'var(--error)', acquired: 'var(--error)', halted: '#d98a00', suspended: '#d98a00',
                             renamed: '#d98a00', no_trades: 'var(--text-muted)', data_issue: 'var(--accent)' }[v] || 'var(--text-muted)');

export default function AdminJobLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [run, setRun] = useState(null);
  const [skips, setSkips] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get('/admin/joblog/runs').then(r => {
      setRuns(r.data.runs);
      setModel(r.data.model || '');
      if (r.data.runs.length) setRun(r.data.runs[0]);
    }).catch(() => setErr('Could not load the job runs'));
    // only models with tool calling (web search runs as a tool), cheapest first
    api.get('/admin/audit/models').then(r => setModels(r.data.models.filter(m => m.tools).sort((a, b) => a.prompt - b.prompt)))
      .catch(() => setErr('Could not load the OpenRouter model list'));
  }, []);

  const loadOverrides = useCallback(() => {
    api.get('/admin/joblog/overrides').then(r => setOverrides(r.data.overrides))
      .catch(() => setErr('Could not load the manual overrides'));
  }, []);
  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const loadSkips = useCallback(() => {
    if (!run) return;
    api.get(`/admin/joblog/skips?run_id=${run.id}`).then(r => setSkips(r.data.skips))
      .catch(() => setErr('Could not load the skipped tickers'));
  }, [run]);
  useEffect(() => { loadSkips(); }, [loadSkips]);

  // poll while any review is still working
  useEffect(() => {
    clearTimeout(timer.current);
    if (skips.some(x => ['queued', 'running'].includes(x.review_status))) timer.current = setTimeout(loadSkips, 5000);
    return () => clearTimeout(timer.current);
  }, [skips, loadSkips]);

  const ask = ids => {
    setMsg(null); setErr(null);
    api.post('/admin/joblog/review', ids ? { instrument_ids: ids } : { run_id: run.id })
      .then(r => { setMsg(`${r.data.queued} queued for research with ${r.data.model}`); loadSkips(); })
      .catch(e => setErr(e.response?.data?.error || 'Could not queue the research'));
  };
  const saveModel = () => {
    setMsg(null); setErr(null);
    api.post('/admin/joblog/settings', { model }).then(r => setMsg(r.data.message))
      .catch(e => setErr(e.response?.data?.error || 'Could not save the model'));
  };
  const setActive = (x, on) => {
    api.post('/admin/joblog/active', { instrument_id: x.instrument_id, active: on })
      .then(() => {
        setSkips(list => list.map(y => y.instrument_id === x.instrument_id ? { ...y, is_active: on } : y));
        loadOverrides();
      })
      .catch(e => setErr(e.response?.data?.error || `Could not update ${x.symbol}`));
  };

  const byReason = skips.reduce((acc, x) => ({ ...acc, [x.reason]: [...(acc[x.reason] || []), x] }), {});
  const price = m => `$${m.prompt.toFixed(2)} / $${m.completion.toFixed(2)}`;

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.heading}>Job Log</div>
        <div style={s.sub}>What each job run did, which tickers it skipped and why. Ask the AI to research the skips, then switch tickers inactive where they are gone.</div>

        <div style={s.section}>
          <div style={s.title}>Research model</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <select style={s.select} value={model} onChange={e => setModel(e.target.value)}>
              {model && !models.some(m => m.id === model) && <option value={model}>{model}</option>}
              {models.map(m => <option key={m.id} value={m.id}>{m.name} - {price(m)} per M tokens</option>)}
            </select>
            <button style={s.btn} onClick={saveModel}>Save</button>
            <span style={s.meta}>Only models that can call tools are listed (web search runs as a tool).</span>
          </div>
        </div>

        <div style={s.section}>
          <div style={s.title}>Runs</div>
          {runs === null && !err && <div style={s.meta}>Loading…</div>}
          {runs && !runs.length && <div style={s.meta}>No runs logged yet - the EOD bars job logs from its next run.</div>}
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            {(runs || []).map(r => (
              <div key={r.id} style={{ ...s.row, cursor: 'pointer', background: run?.id === r.id ? 'var(--accent-dim)' : 'none' }}
                   onClick={() => setRun(r)}>
                <span style={{ ...s.mono, minWidth: '110px' }}>{r.job}</span>
                <span style={s.meta}>{when(r.started_at)}</span>
                <span style={{ color: statusColor(r.status), fontWeight: 500 }}>{r.status}</span>
                {r.summary && <span style={s.meta}>total {r.summary.total} · ok {r.summary.ok} · failed {r.summary.failed} · skipped {r.summary.skipped}</span>}
              </div>
            ))}
          </div>
          {run?.error && <pre style={{ ...s.err, whiteSpace: 'pre-wrap' }}>{run.error}</pre>}
        </div>

        {run && (
          <div style={s.section}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ ...s.title, marginBottom: 0 }}>Skipped in {run.job} · {when(run.started_at)} ({skips.length})</div>
              {skips.length > 0 && <button style={s.btn} onClick={() => ask(null)}>Why were these skipped?</button>}
            </div>
            {msg && <div style={{ ...s.meta, marginBottom: '8px' }}>{msg}</div>}
            {!skips.length && <div style={s.meta}>Nothing skipped in this run.</div>}
            {Object.entries(byReason).map(([reason, list]) => (
              <div key={reason} style={{ marginBottom: '14px' }}>
                <div style={{ ...s.mono, fontSize: '12px', fontWeight: 600, margin: '6px 0' }}>{reason} ({list.length})</div>
                {list.map((x, i) => (
                  <div key={`${x.symbol}-${i}`} style={s.row}>
                    <a style={{ ...s.mono, fontWeight: 600 }} href={`/company?ticker=${encodeURIComponent(`${x.symbol}:${x.exchange}`)}`} target="_blank" rel="noreferrer">
                      {x.symbol}:{x.exchange}</a>
                    <span style={s.meta}>{x.name || ''} {x.asset_type ? `· ${x.asset_type}` : ''}</span>
                    <span style={s.meta}>{x.d}</span>
                    <span style={s.meta}>{x.detail}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: x.is_active ? 'var(--text)' : 'var(--error)' }}>
                        {x.is_active ? 'active' : 'inactive'}</span>
                      {x.instrument_id && <button style={s.btn} onClick={() => setActive(x, !x.is_active)}>
                        {x.is_active ? 'Set inactive' : 'Set active'}</button>}
                      {x.instrument_id && <button style={s.btn} onClick={() => ask([x.instrument_id])}>Ask AI</button>}
                    </span>
                    {x.review_status && (
                      <div style={s.answer}>
                        {['queued', 'running'].includes(x.review_status) && <span style={s.meta}>AI researching ({x.review_status})…</span>}
                        {x.review_status === 'error' && <span style={s.err}>AI error: {x.review_error}</span>}
                        {x.review_status === 'done' && <>
                          <b style={{ color: verdictColor(x.verdict), textTransform: 'uppercase' }}>{x.verdict.replace('_', ' ')}</b>
                          {' '}{x.answer}
                          {(x.sources || []).map((src, k) => (
                            <span key={k}> · <a href={src.url} target="_blank" rel="noreferrer">{src.label || src.url}</a></span>
                          ))}
                          <span style={s.meta}> · {x.model} · {when(x.reviewed_at)}</span>
                        </>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        <div style={s.section}>
          <div style={s.title}>Set inactive by hand ({overrides.length})</div>
          <div style={{ ...s.meta, marginBottom: '8px' }}>The nightly symbol job leaves these inactive even while they are still listed. Set active to undo.</div>
          {!overrides.length && <div style={s.meta}>None.</div>}
          {overrides.map(o => (
            <div key={o.instrument_id} style={s.row}>
              <a style={{ ...s.mono, fontWeight: 600 }} href={`/company?ticker=${encodeURIComponent(`${o.symbol}:${o.exchange}`)}`} target="_blank" rel="noreferrer">
                {o.symbol}:{o.exchange}</a>
              <span style={s.meta}>{o.name || ''}</span>
              <span style={s.meta}>{when(o.set_at)} · {o.set_by}</span>
              <span style={{ marginLeft: 'auto' }}>
                <button style={s.btn} onClick={() => setActive(o, true)}>Set active</button></span>
              {o.note && <div style={s.answer}>{o.note}</div>}
            </div>
          ))}
        </div>
        {err && <div style={s.err}>{err}</div>}
      </div>
    </div>
  );
}
